import { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import {
  courses as courseQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import { UploadPhraseListImage } from "../../../utils/imageUpload";
import { useApp, useSubscription } from "../../../hooks";
import useServices from "../../../hooks/useServices";
import useAudioPlayer from "../../../hooks/useAudioPlayer";
import config from "../../../config";
import { getCourse } from "@nualang/nualang-api-and-queries/APIs/Courses";
import { getCourseSectionTopic } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopics";
import {
  getCourseSectionTopicPhraseList,
  getCourseSectionTopicPhraseLists,
  updateCourseSectionTopicPhraseList,
  createCourseSectionTopicPhraseList as createCourseSectionTopicPhraseListAPI,
} from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicPhraseLists";
import {
  createCourseSectionTopicPhrase as createCourseSectionTopicPhraseAPI,
  updateCourseSectionTopicPhrase as updateCourseSectionTopicPhraseAPI,
  deleteCourseSectionTopicPhrase as deleteCourseSectionTopicPhraseAPI,
  batchCreateCourseSectionTopicPhrases as batchCreateCourseSectionTopicPhrasesAPI,
  batchDeleteCourseSectionTopicPhrases as batchDeleteCourseSectionTopicPhrasesAPI,
} from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicPhrases";
import { courses as courseHelpers } from "../../../utils/helpers";
import { highlightSpeechText, getForLangCharacters } from "../../../utils/index";
import { makeChatGptApiRequest } from "@nualang/nualang-api-and-queries/APIs/Services";
import { UploadPhraseAudio } from "../../../utils/audioUpload";
import PhraseListEditor from "@nualang/nualang-ui-components/dist/Editors/PhraseList/PhraseList";
import useConfirm from "@nualang/nualang-ui-components/dist/hooks/useConfirm";
import { useQueryClient } from "@tanstack/react-query";
import { phraseListKeys } from "@nualang/nualang-api-and-queries/Queries/courses";
import { DataType, invalidateCache } from "@nualang/nualang-api-and-queries/APIs/invalidation";

import logger from "../../../logger";

function pickRandomVoice(voices) {
  const allVoices = Object.entries(voices || {}).flatMap(([, dialect]) => {
    if (!Array.isArray(dialect?.voiceOptions)) return [];
    return dialect.voiceOptions
      .filter((v) => v?.name)
      .map((v) => ({ name: v.name, languageCode: dialect.languageCode }));
  });
  return allVoices.length > 0
    ? allVoices[Math.floor(Math.random() * allVoices.length)]
    : null;
}

function EditPhraseList(props) {
  const { t } = useTranslation();
  const [tts, translate] = useServices();
  const [isOpen, setIsOpen] = useState(true);
  const [isCreatePhraseDialogOpen, setIsCreatePhraseDialogOpen] = useState(false);
  const [isUploadPhrasesDialogOpen, setIsUploadPhrasesDialogOpen] = useState(false);
  const [timeoutArray, setTimeoutArray] = useState([]);

  const {
    authData: { username, attributes },
    navigate,
    verificationStatus,
  } = props;

  const { addToQueue } = useAudioPlayer();
  const params = useParams();
  const { classroomId, courseId, sectionId, topicId, phraseListId } = params;

  const queryClient = useQueryClient();
  const { openSnackbar, handleOpenModerateDialog, startLoading, stopLoading } = useApp();
  const [confirm] = useConfirm(t);
  const { subscription } = useSubscription();


  const phraseListQuery = courseQuerys.useCourseSectionTopicPhraseList(
    async (cId, sId, tId, plId, filters) => {
      const res = await getCourseSectionTopicPhraseList(cId, sId, tId, plId, filters);
      const item = res?.Item || null;
      if (item?.phrases) {
        item.phrases = [...item.phrases].sort((a, b) => (a.idx ?? 0) - (b.idx ?? 0));
      }
      return item;
    },
    { courseId, sectionId, topicId, phraseListId, filters: { includePhrases: "true" } },
    {
      enabled: !!courseId && !!sectionId && !!topicId && !!phraseListId,
      staleTime: 0,
    },
  );

  const phraseList = phraseListQuery.isSuccess && phraseListQuery.data?.phraseListId
    ? phraseListQuery.data
    : null;

  const phraseListsQuery = courseQuerys.useCourseSectionTopicPhraseLists(
    async (cId, sId, tId) => {
      const res = await getCourseSectionTopicPhraseLists(cId, sId, tId);
      return res;
    },
    { courseId, sectionId, topicId },
    { enabled: !!courseId && !!sectionId && !!topicId },
  );

  const otherPhraseLists = phraseListsQuery.isSuccess
    ? (phraseListsQuery.data?.Items || []).filter(
      (pl) => pl.phraseListId !== phraseListId
    )
    : [];

  const handleClose = () => {
    setIsOpen(false);
    if (classroomId) {
      navigate(`/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}`);
    } else {
      navigate(`/courses/${courseId}/${sectionId}/${topicId}`);
    }
  };

  const handlePlayPhraseList = () => {
    if (classroomId) {
      navigate(`/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}/phrase-lists/${phraseListId}`);
    } else {
      navigate(`/courses/${courseId}/${sectionId}/${topicId}/phrase-lists/${phraseListId}`);
    }
  };

  const courseQuery = courseQuerys.useCourse(
    async (cId) => {
      const course = await getCourse(cId);
      return courseHelpers.formatCourse(course.Item, props.authData);
    },
    { courseId, username: props.authData.username },
    { enabled: !!courseId },
  );

  const course =
    courseQuery.isSuccess && courseQuery.data?.courseId ? courseQuery.data : {};
  const { learnLang = "", forLang = "", isCreator, isMember } = course;
  const isCourseLoaded = courseQuery.isSuccess && !!course.courseId;

  const topicQuery = courseQuerys.useTopic(
    async (cId, sId, tId) => {
      const topic = await getCourseSectionTopic(cId, sId, tId);
      return courseHelpers.formatTopic(topic.Item, props.authData);
    },
    { courseId, sectionId, topicId, username: props.authData.username },
    { enabled: !!courseId && !!sectionId && !!topicId },
  );

  const courseSectionTopic =
    topicQuery.isSuccess && topicQuery.data?.topicId ? topicQuery.data : {};

  const isUserInternal = config.internalDomains.some((domain) =>
    (attributes.email || "").endsWith(domain),
  );

  const createPhraseMutation = courseQuerys.useCreateCourseSectionTopicPhrase(
    createCourseSectionTopicPhraseAPI,
    {
      onMutate: () => startLoading(),
      onSuccess: async (data, variables) => {
        setIsCreatePhraseDialogOpen(false);
        await invalidateCache(DataType.PHRASE_LIST, {
          courseId,
          sectionId,
          topicId,
          phraseListId,
        });
        queryClient.setQueryData(
          phraseListKeys.item(variables.courseId, variables.sectionId, variables.topicId, variables.values.phraseListId),
          (old) => old ? { ...old, phrases: [...(old.phrases || []), data.Item] } : old,
        );
        openSnackbar("created", "success");
      },
      onError: () => openSnackbar("problem_creating", "error"),
      onSettled: () => stopLoading(),
    },
  );

  const updatePhraseMutation = courseQuerys.useUpdateCourseSectionTopicPhrase(
    updateCourseSectionTopicPhraseAPI,
    {
      onSuccess: async (data, variables) => {
        queryClient.setQueryData(
          phraseListKeys.item(variables.courseId, variables.sectionId, variables.topicId, variables.phraseListId),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              phrases: (old.phrases || []).map((p) =>
                p.phraseId === variables.phraseId ? { ...p, ...variables.values } : p
              ),
            };
          },
        );
        await invalidateCache(DataType.PHRASE_LIST, {
          courseId,
          sectionId,
          topicId,
          phraseListId,
        });
        queryClient.invalidateQueries({
          queryKey: phraseListKeys.item(courseId, sectionId, topicId, phraseListId),
        });
        openSnackbar("updated", "success");
      },
      onError: () => openSnackbar("problem_updating", "error"),
    },
  );

  const deletePhraseMutation = courseQuerys.useDeleteCourseSectionTopicPhrase(
    deleteCourseSectionTopicPhraseAPI,
    {
      onMutate: () => startLoading(),
      onSuccess: async (data, variables) => {
        await invalidateCache(DataType.PHRASE_LIST, {
          courseId,
          sectionId,
          topicId,
          phraseListId,
        });
        queryClient.setQueryData(
          phraseListKeys.item(variables.courseId, variables.sectionId, variables.topicId, variables.phraseListId),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              phrases: (old.phrases || []).filter((p) => p.phraseId !== variables.phraseId),
            };
          },
        );
        openSnackbar("deleted", "success");
      },
      onError: () => openSnackbar("problem_deleting", "error"),
      onSettled: () => stopLoading(),
    },
  );

  const updatePhraseListMutation = courseQuerys.useUpdateCourseSectionTopicPhraseList(
    updateCourseSectionTopicPhraseList,
    {
      onSuccess: () => openSnackbar("updated", "success"),
      onError: () => openSnackbar("problem", "error"),
    },
  );

  const createPhraseListMutation = courseQuerys.useCreateCourseSectionTopicPhraseList(
    createCourseSectionTopicPhraseListAPI,
  );

  const handleSpeak = async (params) => {
    try {
      const {
        text, learnLanguage, voiceName, mute, callback,
        textContainer, languageCode, voiceSpeed, pitch,
      } = params;
      const result = await tts(text, learnLanguage, voiceName, languageCode, voiceSpeed, pitch);
      if (!mute && result?.audioUrl) {
        const highlightCallback = () =>
          highlightSpeechText(textContainer, result.speechMarks, setTimeoutArray);
        addToQueue(result.audioUrl, callback, highlightCallback);
      } else if (result?.errorMessage) {
        throw new Error(result.errorMessage);
      }
      return result;
    } catch (error) {
      logger.error(error);
      return null;
    }
  };

  const handleAddPhrase = async (values) => {
    if (!phraseList?.phraseListId) {
      logger.error("handleAddPhrase: phraseList not loaded yet");
      openSnackbar("problem_creating", "error");
      return null;
    }

    if (learnLang === forLang) {
      if (!values.phrase) {
        openSnackbar("missing_phrase_details", "error");
        return null;
      }
    } else if (learnLang !== forLang) {
      if (
        !values.phrase ||
        !(Array.isArray(values.translations) && values.translations.length > 0)
      ) {
        openSnackbar("missing_phrase_details", "error");
        return null;
      }
    }

    try {
      if (values.image && typeof values.image === "string" && values.image.startsWith("data:")) {
        values.imageWarnDetails = {};
        const base64String = values.image.split(",");
        const [image, imageS3Key, moderated, warn, moderatedLabels] = await UploadPhraseListImage(
          base64String[1] || base64String[0],
          `${phraseListId}-new-${Date.now()}`,
        );
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return null;
            }
            values.imageWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return null;
          }
        }
        if (image) {
          values.image = image;
          values.imageS3Key = imageS3Key;
        } else {
          delete values.image;
          delete values.imageS3Key;
        }
      }

      delete values.selectionMethod;

      const result = await createPhraseMutation.mutateAsync({
        courseId,
        sectionId,
        topicId,
        values: { ...values, idx: Date.now(), phraseListId: phraseList.phraseListId },
      });
      return result?.Item || null;
    } catch (error) {
      return null;
    }
  };

  const updatePhrase = async (phraseId, updatedPhrase) => {
    const isUpdatingVoices =
      updatedPhrase && updatedPhrase.voices && updatedPhrase.voices.length > 0;
    if (!isUpdatingVoices) {
      if (learnLang === forLang) {
        if (!updatedPhrase.phrase) {
          openSnackbar("missing_phrase_details", "error");
          return undefined;
        }
      } else if (learnLang !== forLang) {
        if (
          !updatedPhrase.phrase ||
          !(Array.isArray(updatedPhrase.translations) && updatedPhrase.translations.length > 0)
        ) {
          openSnackbar("missing_phrase_details", "error");
          return undefined;
        }
      }
    }

    try {
      if (updatedPhrase.image && typeof updatedPhrase.image === "string" && updatedPhrase.image.startsWith("data:")) {
        updatedPhrase.imageWarnDetails = {};
        const base64String = updatedPhrase.image.split(",");
        const [image, imageS3Key, moderated, warn, moderatedLabels] = await UploadPhraseListImage(
          base64String[1] || base64String[0],
          `${phraseListId}-${phraseId}-${Date.now()}`,
        );
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return undefined;
            }
            updatedPhrase.imageWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return undefined;
          }
        }
        if (image) {
          updatedPhrase.image = image;
          updatedPhrase.imageS3Key = imageS3Key;
        } else {
          updatedPhrase.image = phraseList?.phrases?.find(p => p.phraseId === phraseId)?.image;
          updatedPhrase.imageS3Key = phraseList?.phrases?.find(p => p.phraseId === phraseId)?.imageS3Key;
        }
      }

      const result = await updatePhraseMutation.mutateAsync({
        courseId,
        sectionId,
        topicId,
        phraseId,
        phraseListId: phraseList.phraseListId,
        values: updatedPhrase,
      });
      return result;
    } catch (error) {
      return undefined;
    }
  };

  const updatePhrases = async (cId, sId, tId, phrases) => {
    try {
      await Promise.all(
        phrases.map((phrase, i) =>
          updateCourseSectionTopicPhraseAPI(
            courseId, sectionId, topicId, phrase.phraseId, { idx: i },
          )
        )
      );

      await invalidateCache(DataType.PHRASE_LIST, {
        courseId,
        sectionId,
        topicId,
        phraseListId: phraseList.phraseListId,
      });

      queryClient.setQueryData(
        phraseListKeys.item(courseId, sectionId, topicId, phraseListId),
        (old) => {
          if (!old) return old;
          return { ...old, phrases };
        }
      );
      openSnackbar("updated", "success");
    } catch (error) {
      logger.error(error);
      openSnackbar("problem_updating", "error");
    }
  };

  const handleRemovePhrase = (phraseId) => {
    deletePhraseMutation.mutate({
      courseId,
      sectionId,
      topicId,
      phraseId,
      phraseListId: phraseList.phraseListId,
    });
  };

  const handleRemovePhrases = async (phraseIds) => {
    startLoading();
    try {
      await batchDeleteCourseSectionTopicPhrasesAPI(courseId, sectionId, topicId, { phraseIds });
      await invalidateCache(DataType.PHRASE_LIST, {
        courseId,
        sectionId,
        topicId,
        phraseListId: phraseList.phraseListId,
      });
      const deletedSet = new Set(phraseIds);
      queryClient.setQueryData(
        phraseListKeys.item(courseId, sectionId, topicId, phraseListId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            phrases: (old.phrases || []).filter((p) => !deletedSet.has(p.phraseId)),
          };
        }
      );
      openSnackbar("deleted", "success");
    } catch (error) {
      logger.error(error);
      openSnackbar("problem_deleting", "error");
    } finally {
      stopLoading();
    }
  };

  const handleSpreadsheetSaveChanges = async (oldPhrases, newPhrases) => {
    if (!phraseList?.phraseListId) return;
    startLoading();
    try {
      const oldPhraseMap = new Map((oldPhrases || []).map((p) => [p.phraseId, p]));
      const newPhraseIdSet = new Set(newPhrases.filter((p) => p.phraseId).map((p) => p.phraseId));

      const phrasesToCreate = newPhrases.filter((p) => !p.phraseId);
      const phrasesToDelete = (oldPhrases || []).filter((p) => !newPhraseIdSet.has(p.phraseId));
      const phrasesToUpdate = newPhrases
        .filter((p) => p.phraseId && oldPhraseMap.has(p.phraseId))
        .map((newPhrase) => ({ oldPhrase: oldPhraseMap.get(newPhrase.phraseId), newPhrase }))
        .filter(({ oldPhrase, newPhrase }) => {
          const omitSystemKeys = (obj) =>
            Object.fromEntries(Object.entries(obj).filter(([k]) => k !== 'phraseId' && k !== 'idx' && k !== 'id'));
          return JSON.stringify(omitSystemKeys(oldPhrase)) !== JSON.stringify(omitSystemKeys(newPhrase));
        });

      const batchCreatePromise = phrasesToCreate.length > 0
        ? batchCreateCourseSectionTopicPhrasesAPI(courseId, sectionId, topicId, {
            phrases: phrasesToCreate.map((values, i) => ({
              ...values,
              idx: Date.now() + i,
              phraseListId: phraseList.phraseListId,
            })),
          })
        : Promise.resolve(null);

      const otherPromises = [
        phrasesToDelete.length > 0
          ? batchDeleteCourseSectionTopicPhrasesAPI(courseId, sectionId, topicId, {
              phraseIds: phrasesToDelete.map((p) => p.phraseId),
            })
          : null,
        ...phrasesToUpdate.map(({ oldPhrase, newPhrase }) =>
          updateCourseSectionTopicPhraseAPI(courseId, sectionId, topicId, oldPhrase.phraseId, newPhrase)
        ),
      ].filter(Boolean);

      const [createResult] = await Promise.all([batchCreatePromise, ...otherPromises]);

      const deletedPhraseIdSet = new Set(phrasesToDelete.map((p) => p.phraseId));
      const updateMap = new Map(phrasesToUpdate.map(({ oldPhrase, newPhrase }) => [oldPhrase.phraseId, newPhrase]));
      queryClient.setQueryData(
        phraseListKeys.item(courseId, sectionId, topicId, phraseListId),
        (old) => {
          if (!old) return old;
          const kept = (old.phrases || []).filter((p) => !deletedPhraseIdSet.has(p.phraseId));
          const updatedKept = kept.map((p) => updateMap.has(p.phraseId) ? { ...p, ...updateMap.get(p.phraseId) } : p);
          const created = createResult?.Items || [];
          return {
            ...old,
            phrases: [...updatedKept, ...created].sort((a, b) => (a.idx ?? 0) - (b.idx ?? 0)),
          };
        }
      );

      await invalidateCache(DataType.PHRASE_LIST, {
        courseId, sectionId, topicId,
        phraseListId: phraseList.phraseListId,
      });
      stopLoading();
      openSnackbar("updated", "success");
    } catch (error) {
      stopLoading();
      logger.error(error);
      openSnackbar("problem_updating", "error");
    }
  };

  const handleUpdatePhraseList = async (phraseListId, values) => {
    try {
      const { phrases, phraseListId: _id, ...cleanValues } = values;
      if (cleanValues.picture && typeof cleanValues.picture === "string" && cleanValues.picture.startsWith("data:")) {
        const base64String = cleanValues.picture.split(",");
        const [picture, pictureS3Key] = await UploadPhraseListImage(
          base64String[1] || base64String[0],
          phraseListId,
        );
        if (picture) {
          cleanValues.picture = picture;
          cleanValues.pictureS3Key = pictureS3Key;
        } else {
          if (phraseList?.picture) {
            cleanValues.picture = phraseList.picture;
            cleanValues.pictureS3Key = phraseList.pictureS3Key;
          } else {
            delete cleanValues.picture;
            delete cleanValues.pictureS3Key;
          }
        }
      }
      const result = await updatePhraseListMutation.mutateAsync({
        courseId,
        sectionId,
        topicId,
        phraseListId,
        values: cleanValues,
        username,
      });
      return result;
    } catch (error) {
      return undefined;
    }
  };

  const handleTransferPhrase = async (phraseIds, targetPhraseListIds) => {
    const ids = Array.isArray(targetPhraseListIds) ? targetPhraseListIds : [targetPhraseListIds];
    const pIds = Array.isArray(phraseIds) ? phraseIds : [phraseIds];
    try {
      const createCalls = pIds.flatMap((phraseId) => {
        const phrase = phraseList.phrases.find(p => p.phraseId === phraseId);
        if (!phrase) return [];
        const { phrase: phraseText, translations, definitions, image, imageS3Key, voices, alternativeVersions } = phrase;
        return ids.map((targetId) =>
          createCourseSectionTopicPhraseAPI(courseId, sectionId, topicId, {
            phrase: phraseText,
            translations,
            definitions,
            image,
            imageS3Key,
            voices,
            alternativeVersions,
            idx: Date.now(),
            phraseListId: targetId,
          })
        );
      });

      const results = await Promise.allSettled(createCalls);
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length > 0) {
        const createdIds = results
          .filter((r) => r.status === "fulfilled" && r.value?.Item?.phraseId)
          .map((r) => r.value.Item.phraseId);
        await Promise.allSettled(
          createdIds.map((id) => deleteCourseSectionTopicPhraseAPI(courseId, sectionId, topicId, id))
        );
        throw new Error(`${failed.length} phrase(s) failed to transfer; changes rolled back`);
      }

      await Promise.all(
        ids.map((targetId) =>
          invalidateCache(DataType.PHRASE_LIST, { courseId, sectionId, topicId, phraseListId: targetId })
        )
      );

      openSnackbar("updated", "success");
    } catch (error) {
      logger.error(error);
      openSnackbar("problem_updating", "error");
    }
  };

  const handleCreatePhraseListFromSelection = async (phraseIds, phraseListName) => {
    const pIds = Array.isArray(phraseIds) ? phraseIds : [phraseIds];
    try {
      const randomVoice = pickRandomVoice(voices);
      const newList = await createPhraseListMutation.mutateAsync({
        courseId,
        sectionId,
        topicId,
        values: {
          phraseListName,
          ...(randomVoice && { voice: randomVoice.name, voiceLanguageCode: randomVoice.languageCode }),
        },
      });
      if (!newList?.Item) throw new Error("Failed to create phrase list");

      const newPhraseListId = newList.Item.phraseListId;
      const results = await Promise.allSettled(
        pIds.map((phraseId) => {
          const phrase = phraseList.phrases.find(p => p.phraseId === phraseId);
          if (!phrase) return Promise.resolve(null);
          const { phrase: phraseText, translations, definitions, image, imageS3Key, voices, alternativeVersions } = phrase;
          return createCourseSectionTopicPhraseAPI(courseId, sectionId, topicId, {
            phrase: phraseText,
            translations,
            definitions,
            image,
            imageS3Key,
            voices,
            alternativeVersions,
            idx: Date.now(),
            phraseListId: newPhraseListId,
          });
        })
      );

      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length > 0) {
        const createdIds = results
          .filter((r) => r.status === "fulfilled" && r.value?.Item?.phraseId)
          .map((r) => r.value.Item.phraseId);
        await Promise.allSettled(
          createdIds.map((id) => deleteCourseSectionTopicPhraseAPI(courseId, sectionId, topicId, id))
        );
        throw new Error(`${failed.length} phrase(s) failed to create; changes rolled back`);
      }

      await invalidateCache(DataType.PHRASE_LIST, {
        courseId, sectionId, topicId,
        phraseListId: newPhraseListId,
      });
      queryClient.invalidateQueries({
        queryKey: phraseListKeys.list(courseId, sectionId, topicId),
      });

      openSnackbar("created", "success");
    } catch (error) {
      logger.error(error);
      openSnackbar("problem_creating", "error");
    }
  };

  const handleUploadAudio = async (name, src) =>
    UploadPhraseAudio(src, `${courseId}|${sectionId}|${topicId}-${name}`);

  const learnLangCharacters = learnLang
    ? config.languages[learnLang.toLowerCase() || "english"].characters
    : [];
  const forLangCharacters = getForLangCharacters(forLang);
  const specialCharacters = [...new Set(learnLangCharacters.concat(forLangCharacters))];

  const voices = useMemo(
    () => (learnLang ? config.languages[learnLang.toLowerCase() || "english"].Voices : []),
    [learnLang],
  );

  const autoVoiceSavedRef = useRef(false);

  useEffect(() => {
    if (!phraseList?.phraseListId || phraseList?.voice || autoVoiceSavedRef.current) return;
    const allVoices = Object.entries(voices || {}).flatMap(([, dialect]) => {
      if (!Array.isArray(dialect?.voiceOptions)) return [];
      return dialect.voiceOptions
        .filter((v) => v?.name)
        .map((v) => ({ name: v.name, languageCode: dialect.languageCode }));
    });
    if (!allVoices.length) return;
    autoVoiceSavedRef.current = true;
    const randomVoice = allVoices[Math.floor(Math.random() * allVoices.length)];
    updateCourseSectionTopicPhraseList(courseId, sectionId, topicId, phraseList.phraseListId, {
      voice: randomVoice.name,
      voiceLanguageCode: randomVoice.languageCode,
    }).then(() => {
      queryClient.setQueryData(
        phraseListKeys.item(courseId, sectionId, topicId, phraseListId),
        (old) => old ? { ...old, voice: randomVoice.name, voiceLanguageCode: randomVoice.languageCode } : old,
      );
    }).catch((err) => {
      autoVoiceSavedRef.current = false;
      logger.error("Failed to auto-save default voice", err);
    });
  }, [phraseList?.phraseListId, phraseList?.voice, voices, courseId, sectionId, topicId, phraseListId, queryClient]);

  if (phraseListQuery.isLoading || !phraseList || !isCourseLoaded) return null;

  return (
    <div>
      <PhraseListEditor
        t={t}
        open={isOpen}
        handleClose={handleClose}
        handlePlayPhraseList={handlePlayPhraseList}
        initialValues={phraseList}
        phrases={phraseList.phrases || []}
        isCreator={isCreator}
        isMember={isMember}
        courseId={courseId}
        sectionId={sectionId}
        topicId={topicId}
        learnLang={learnLang}
        forLang={forLang}
        voices={voices}
        handleSpeak={handleSpeak}
        handleTranslate={translate}
        userAttributes={attributes}
        learnLangCharacters={specialCharacters}
        forLangCharacters={specialCharacters}
        fileSizeLimit={config.fileSizeLimit}
        subscription={subscription}
        makeChatGptApiRequest={makeChatGptApiRequest}
        siteLanguage={attributes?.locale || "en"}
        isUserInternal={isUserInternal}
        verificationStatus={verificationStatus}
        handleRemovePhrase={handleRemovePhrase}
        handleRemovePhrases={handleRemovePhrases}
        uploadAudio={handleUploadAudio}
        updatePhrase={updatePhrase}
        handleUpdatePhrases={updatePhrases}
        isUploadPhrasesDialogOpen={isUploadPhrasesDialogOpen}
        setIsUploadPhrasesDialogOpen={setIsUploadPhrasesDialogOpen}
        isCreatePhraseDialogOpen={isCreatePhraseDialogOpen}
        setIsCreatePhraseDialogOpen={setIsCreatePhraseDialogOpen}
        handleAddPhrase={handleAddPhrase}
        topicName={courseSectionTopic.topicName || ""}
        topicGoal={courseSectionTopic.topicGoal || ""}
        difficulty={courseSectionTopic.difficulty || ""}
        handleSpreadsheetSaveChanges={handleSpreadsheetSaveChanges}
        onSubmit={(values, helpers) =>
          handleUpdatePhraseList(phraseList.phraseListId, values, helpers)
        }
        otherPhraseLists={otherPhraseLists}
        otherPhraseListsLoading={phraseListsQuery.isLoading}
        handleTransferPhrase={handleTransferPhrase}
        handleCreatePhraseListFromSelection={handleCreatePhraseListFromSelection}
        openSnackbar={openSnackbar}
      />
    </div>
  );
}

EditPhraseList.propTypes = {
  navigate: PropTypes.func,
  authData: PropTypes.object,
  verificationStatus: PropTypes.string,
  handleCreatePhraseListFromSelection: PropTypes.func,
};

export default EditPhraseList;